import { FC, useEffect, useState } from 'react';
import _ from 'lodash';
import { useLazyGetDeviceHealthQuery } from 'store/services';
import { Device, DeviceDetailedHealth } from 'types';
import { statusClassState } from 'components/Devices/GenericStatusIcon/GenericStatusIcon';
import styles from './DeviceHealth.module.scss';
import DeviceHealthMainCard from './cards/DeviceHealthMainCard/DeviceHealthMainCard';
import DeviceHealthTelemetryCard from './cards/DeviceHealthTelemetryCard/DeviceHealthTelemetryCard';
import DeviceHealthBatteryCard from './cards/DeviceHealthBatteryCard/DeviceHealthBatteryCard';
import DeviceHealthSignalCard from './cards/DeviceHealthSignalCard/DeviceHealthSignalCard';
import DeviceHealthVoltageTemperatureCard from './cards/DeviceHealthVoltageTemperatureCard/DeviceHealthVoltageTemperatureCard';
import { DeviceHealthMissingCard } from './cards/DeviceHealthMissingCard/DeviceHealthMissingCard';

export interface DeviceHealthProps {
    device: Device;
}

const HEALTH_FLAGS = {
    new_loc: 'Move to new location',
    offline: 'Offline',
    offline_dead: 'Offline (dead)',
    offline_recent: 'Offline (recent)',
    escalate: 'Escalate',
    fplmn_banned: 'Network banned',
    battery_err: 'Low battery',
};

const calcHealthFlags = (health: DeviceDetailedHealth) => {
    const output = [];
    if (health?.new_loc) output.push(HEALTH_FLAGS.new_loc);
    if (health?.offline) output.push(HEALTH_FLAGS.offline);
    if (health?.offline_dead) output.push(HEALTH_FLAGS.offline_dead);
    if (health?.offline_recent) {
        output.push(HEALTH_FLAGS.offline_recent);
    }
    if (health?.escalate) output.push(HEALTH_FLAGS.escalate);
    if (health?.fplmn_banned) output.push(HEALTH_FLAGS.fplmn_banned);
    if (health?.battery_err) output.push(HEALTH_FLAGS.battery_err);
    return output;
};

const getRsrpClass = (rsrp: number): statusClassState => {
    if (rsrp >= -90) return 'success';
    if (rsrp >= -105) return 'warning';
    return 'alert';
};

const getRsrqClass = (rsrq: number): statusClassState => {
    if (rsrq >= -9) return 'success';
    if (rsrq >= -12) return 'warning';
    return 'alert';
};

const calcWifiData = (health: DeviceDetailedHealth) => {
    let rsrp = 0;
    let rsrq = 0;
    let rsrpClass: statusClassState = null;
    let rsrqClass: statusClassState = null;
    if (health.rsrp && health.rsrq) {
        rsrp = _.sum(health.rsrp) / health.rsrp.length;
        rsrq = _.sum(health.rsrq) / health.rsrq.length;
    } else if (health.signalHist.length > 0) {
        rsrp =
            _.sum(health.signalHist.map((h) => h.rsrp)) /
            health.signalHist.length;
        rsrq =
            _.sum(health.signalHist.map((h) => h.rsrq)) /
            health.signalHist.length;
    }

    rsrpClass = getRsrpClass(rsrp);
    rsrqClass = getRsrqClass(rsrq);
    return {
        rsrp,
        rsrq,
        rsrpClass,
        rsrqClass,
    };
};

const calcHealthRatingClass = (h: number): statusClassState => {
    if (h >= 75) return 'success';
    if (h >= 50) return 'warning';
    return 'alert';
};

export const DeviceHealth: FC<DeviceHealthProps> = ({
    device,
}: DeviceHealthProps) => {
    const [queryDeviceHealth] = useLazyGetDeviceHealthQuery();
    const [healthData, setHealthData] = useState<DeviceDetailedHealth | null>(
        null
    );
    const [healthFlags, setHealthFlags] = useState<string[]>([]);
    const [rsrp, setRsrp] = useState<number | null>(null);
    const [rsrq, setRsrq] = useState<number | null>(null);
    const [rsrpClass, setRsrpClass] = useState<statusClassState>('alert');
    const [rsrqClass, setRsrqClass] = useState<statusClassState>('alert');
    const [displayGraphsFromWifiDevice, setDisplayGraphsFromWifiDevice] =
        useState<boolean>(false);
    const [healthRatingClass, setHealthRatingClass] =
        useState<statusClassState>(null);
    const [successRateClass, setSuccessRateClass] =
        useState<statusClassState>(null);
    const [offPropClass, setOffPropClass] = useState<statusClassState>(null);

    useEffect(() => {
        if (healthData !== null) return;
        queryDeviceHealth({
            deviceId: device.deviceId,
        }).then(({ data, isSuccess }) => {
            if (isSuccess) setHealthData(data);
        });
    }, [healthData, device.deviceId, queryDeviceHealth]);

    useEffect(() => {
        // secondary calculations
        if (healthData === null) return;
        setHealthFlags(calcHealthFlags(healthData));
        if (!device.product_id.includes('WIFI')) {
            const wifiData = calcWifiData(healthData);
            setRsrp(wifiData.rsrp);
            setRsrq(wifiData.rsrq);
            setRsrpClass(wifiData.rsrpClass);
            setRsrqClass(wifiData.rsrqClass);
            setDisplayGraphsFromWifiDevice(true);
        }
        setHealthRatingClass(
            calcHealthRatingClass(parseInt(healthData.healthy))
        );
        setSuccessRateClass(calcHealthRatingClass(healthData.rate * 100));
        setOffPropClass(calcHealthRatingClass(100 - healthData.off_prop));
    }, [healthData, device.product_id]);

    return (
        <div className={styles.DeviceHealth} data-testid="DeviceHealth">
            {!device.virtual && healthData ? (
                <>
                    <DeviceHealthMainCard
                        device={device}
                        healthData={healthData}
                        healthFlags={healthFlags}
                        healthRatingClass={healthRatingClass}
                        rsrpClass={rsrpClass}
                        rsrqClass={rsrqClass}
                        successRateClass={successRateClass}
                        offPropClass={offPropClass}
                        rsrp={rsrp}
                        rsrq={rsrq}
                    />
                    {displayGraphsFromWifiDevice && (
                        <DeviceHealthTelemetryCard health={healthData} />
                    )}
                    <DeviceHealthBatteryCard health={healthData} />
                    {displayGraphsFromWifiDevice && (
                        <DeviceHealthSignalCard health={healthData} />
                    )}
                    <DeviceHealthVoltageTemperatureCard health={healthData} />
                </>
            ) : (
                <DeviceHealthMissingCard device={device} />
            )}
        </div>
    );
};
